<template>
  <!-- 青少年模式悬浮卡片：激活且未锁定时显示，右下角 -->
  <transition name="minorCardIn">
    <div v-if="showBar && !collapsed" class="minor-card" :class="{ 'is-curfew': inCurfew }">
      <div class="mc-head">
        <span class="mc-badge">
          <svg class="mc-shield" viewBox="0 0 24 24" width="15" height="15" aria-hidden="true">
            <path d="M12 2 4 5v6c0 5 3.4 8.5 8 11 4.6-2.5 8-6 8-11V5l-8-3z" fill="currentColor"/>
          </svg>
        </span>
        <span class="mc-title">青少年模式</span>
        <button class="mc-fold" type="button" @click="collapsed = true">收起</button>
      </div>
      <!-- 注：游客不再强制开启青少年模式，后端 minor_mode=0 → active=false，
           本卡片对游客整体不渲染。原先的"未登录已强制开启"游客态区块已删除。 -->
      <div class="mc-body">
        <div class="mc-label">{{ inCurfew ? '宵禁中 · 距解禁' : '今日剩余可用' }}</div>
        <div class="mc-time">{{ cardTime }}</div>
        <div class="mc-note">{{ inCurfew ? curfewNote : '时长用尽将锁定，需家长密码临时使用' }}</div>
      </div>
    </div>
    <button v-else-if="showBar && collapsed" class="mc-tab" :class="{ 'is-curfew': inCurfew }" type="button" @click="collapsed = false">
      <svg class="mc-shield" viewBox="0 0 24 24" width="14" height="14" aria-hidden="true">
        <path d="M12 2 4 5v6c0 5 3.4 8.5 8 11 4.6-2.5 8-6 8-11V5l-8-3z" fill="currentColor"/>
      </svg>
      <span class="mc-tab-text">{{ inCurfew ? '宵禁' : cardTime }}</span>
    </button>
  </transition>

  <!-- 超时锁屏遮罩：每日时长用完时全屏拦截 -->
  <div v-if="showOverlay" class="minor-lock-mask">
    <div class="minor-lock-card">
      <div class="minor-lock-icon">
        <svg viewBox="0 0 24 24" width="42" height="42" fill="none" stroke="currentColor"
             stroke-width="1.8" stroke-linecap="round" aria-hidden="true">
          <circle cx="12" cy="13" r="8"/>
          <path d="M12 9v4l2.5 2"/>
          <path d="M9 2h6"/>
        </svg>
      </div>
      <h3>今日使用时长已用完</h3>

      <template v-if="hasParentPwd">
        <p class="minor-lock-tip">输入家长密码，临时使用 30 分钟。</p>
        <input
          class="minor-lock-input"
          type="password"
          v-model="grantPwd"
          placeholder="请输入家长密码"
          @keyup.enter="grantTemp"
        />
        <p v-if="grantErr" class="minor-lock-err">{{ grantErr }}</p>
        <button class="minor-lock-btn primary" :disabled="granting" @click="grantTemp">
          {{ granting ? '授权中…' : '临时使用 30 分钟' }}
        </button>
      </template>

      <template v-else>
        <p class="minor-lock-tip">首次使用请设置家长密码（至少 6 位），设置后立即解锁 30 分钟。</p>
        <input
          class="minor-lock-input"
          type="password"
          v-model="newPwd"
          placeholder="设置家长密码"
          @keyup.enter="grantTemp"
        />
        <input
          class="minor-lock-input"
          type="password"
          v-model="confirmPwd"
          placeholder="确认家长密码"
          @keyup.enter="grantTemp"
        />
        <p v-if="grantErr" class="minor-lock-err">{{ grantErr }}</p>
        <button class="minor-lock-btn primary" :disabled="granting" @click="grantTemp">
          {{ granting ? '授权中…' : '设置并临时使用 30 分钟' }}
        </button>
      </template>

      <button class="minor-lock-btn ghost" @click="logout">退出登录</button>
    </div>
  </div>
</template>

<script>
import { getRealnameStatus, minorHeartbeat, grantTempUse } from '../api/realname';
import router from '../router';

export default {
  name: 'MinorCountdown',
  data() {
    return {
      active: false,
      cfg: {},
      remainSeconds: 0,
      locked: false,
      hasParentPwd: false,
      grantPwd: '',
      newPwd: '',
      confirmPwd: '',
      grantErr: '',
      granting: false,
      collapsed: false,
      isLoggedIn: !!localStorage.getItem('token'),
      now: Date.now(),
      lastBeatTs: Date.now(),
      tickTimer: null,
      beatTimer: null
    };
  },
  computed: {
    showBar() {
      return this.active && !this.locked;
    },
    // 锁屏遮罩仅对已登录用户生效：游客无法调用 grantTempUse 解锁，
    // 弹给游客等于把人锁死在页面上。
    showOverlay() {
      return this.active && this.locked && this.isLoggedIn;
    },
    inCurfew() {
      if (!this.active || !this.cfg) return false;
      const h = new Date(this.now).getHours();
      const s = +this.cfg.night_start;
      const e = +this.cfg.night_end;
      if (s === e) return false;
      if (s > e) return h >= s || h < e;
      return h >= s && h < e;
    },
    curfewRemain() {
      if (!this.inCurfew) return 0;
      const now = new Date(this.now);
      const e = +this.cfg.night_end;
      const target = new Date(now);
      target.setHours(e, 0, 0, 0);
      if (target.getTime() <= now.getTime()) {
        target.setDate(target.getDate() + 1);
      }
      return Math.max(0, Math.floor((target - now) / 1000));
    },
    barText() {
      if (this.inCurfew) {
        return '宵禁中 · 距解禁 ' + this.fmt(this.curfewRemain);
      }
      if (this.locked) {
        return '今日使用时长已用完';
      }
      return '青少年模式 · 今日剩余 ' + this.fmt(this.remainSeconds);
    },
    cardTime() {
      if (this.inCurfew) return this.fmt(this.curfewRemain);
      if (this.locked) return '已用完';
      return this.fmt(this.remainSeconds);
    },
    curfewNote() {
      const s = this.cfg && this.cfg.night_start ? this.cfg.night_start : 22;
      const e = this.cfg && this.cfg.night_end ? this.cfg.night_end : 6;
      return '每日 ' + s + ':00–' + e + ':00 为宵禁时段，暂停使用';
    }
  },
  mounted() {
    this.fetchStatus();
    this.tickTimer = setInterval(this.tick, 1000);
    this.beatTimer = setInterval(this.heartbeat, 60000);
    window.addEventListener('minor-mode-changed', this.fetchStatus);
    window.addEventListener('focus', this.fetchStatus);
  },
  beforeUnmount() {
    if (this.tickTimer) clearInterval(this.tickTimer);
    if (this.beatTimer) clearInterval(this.beatTimer);
    window.removeEventListener('minor-mode-changed', this.fetchStatus);
    window.removeEventListener('focus', this.fetchStatus);
  },
  methods: {
    fmt(s) {
      s = Math.max(0, Math.floor(s));
      const h = Math.floor(s / 3600);
      const m = Math.floor((s % 3600) / 60);
      const sec = s % 60;
      if (h > 0) {
        return h + '时' + String(m).padStart(2, '0') + '分' + String(sec).padStart(2, '0') + '秒';
      }
      return m + '分' + String(sec).padStart(2, '0') + '秒';
    },
    applyStatus(d) {
      if (!d) return;
      if (d.minor_mode) {
        this.active = true;
        this.cfg = d.config || {};
        this.hasParentPwd = !!d.has_parent_pwd;
        // 防御性兜底：游客即使被判定为生效，也不计时、不锁屏。
        // 游客无 usage 数据，倒到 0 会触发锁屏遮罩，而 grantTempUse 对游客返回 401，
        // 会被永久锁死在页面上。真正的时长约束在登录后生效。
        if (!this.isLoggedIn) {
          this.remainSeconds = 0;
          this.locked = false;
          return;
        }
        if (d.usage) {
          // 优先使用后端精确秒，避免刷新时按整数分钟向上取整导致时间回弹
          this.remainSeconds = (typeof d.usage.remain_seconds === 'number')
            ? d.usage.remain_seconds
            : d.usage.remain_minutes * 60;
          this.locked = !!d.usage.locked;
        } else {
          this.remainSeconds = (+this.cfg.daily_use_limit_min || 40) * 60;
          this.locked = false;
        }
      } else {
        this.active = false;
      }
    },
    async fetchStatus() {
      // 每次拉状态时同步登录态：本组件常驻 App.vue，登录/登出后不重新挂载，
      // 若只在 data 初始化时读一次 token，登录后仍会被当成游客。
      this.isLoggedIn = !!localStorage.getItem('token');
      // 游客同样请求状态接口：后端对未登录用户返回 minor_mode=0（不强制开启），
      // 但若用户在别处开启过或后端配置变更，这里也能立即同步。
      try {
        const res = await getRealnameStatus();
        if (res.code === 1) {
          this.applyStatus(res.data);
        } else {
          this.active = false;
        }
      } catch (e) {
        this.active = false;
      }
    },
    tick() {
      this.now = Date.now();
      // 游客不计时（后端也不累计），避免倒到 0 触发无法解除的锁屏
      if (!this.isLoggedIn) return;
      // 仅非宵禁时段才扣减每日剩余时长
      if (this.active && !this.inCurfew && !this.locked) {
        this.remainSeconds = Math.max(0, this.remainSeconds - 1);
        if (this.remainSeconds <= 0) {
          this.locked = true;
        }
      }
    },
    async heartbeat() {
      if (!this.active) return;
      // 游客调用必 401，且后端不累计，直接跳过避免无意义请求
      if (!this.isLoggedIn) return;
      // 上报真实流逝秒数，避免本地计时与后端累计漂移
      const now = Date.now();
      const sec = Math.floor((now - this.lastBeatTs) / 1000);
      this.lastBeatTs = now;
      // 已锁屏：不计费，仅刷新锚点，避免挂机空转消耗临时授权时长
      if (this.locked) {
        return;
      }
      try {
        const res = await minorHeartbeat(sec);
        if (res.code === 1) {
          if (res.data.active) {
            this.remainSeconds = res.data.remain_seconds;
            this.locked = res.data.locked;
          } else {
            this.active = false;
          }
        }
      } catch (e) {
        // 网络异常忽略，下一分钟重试
      }
    },
    logout() {
      localStorage.removeItem('token');
      localStorage.removeItem('userInfo');
      if (router && router.push) {
        router.push('/login');
      } else {
        window.location.reload();
      }
    },
    async grantTemp() {
      let pwd = '';
      let newPwd = '';
      if (this.hasParentPwd) {
        if (!this.grantPwd) {
          this.grantErr = '请输入家长密码';
          return;
        }
        pwd = this.grantPwd;
      } else {
        if (!this.newPwd) {
          this.grantErr = '请输入家长密码';
          return;
        }
        if (this.newPwd.length < 6) {
          this.grantErr = '家长密码至少 6 位';
          return;
        }
        if (this.newPwd !== this.confirmPwd) {
          this.grantErr = '两次输入的密码不一致';
          return;
        }
        newPwd = this.newPwd;
      }
      this.granting = true;
      this.grantErr = '';
      try {
        const res = await grantTempUse(pwd, newPwd);
        if (res.code === 1) {
          this.grantPwd = '';
          this.newPwd = '';
          this.confirmPwd = '';
          this.grantErr = '';
          // 直接用返回的授权后状态立即解锁（含 30 分钟剩余），不再发二次请求，避免延迟
          this.applyStatus(res.data);
          this.lastBeatTs = Date.now();
        } else {
          this.grantErr = res.msg || '授权失败';
        }
      } catch (e) {
        // 注意：api/request.js 的响应拦截器会把 code !== 1 的业务错误
        // 也 reject 成 Error(res.msg)，所以业务失败同样会走到这里。
        // 直接写死"网络异常"会把"家长密码错误"这类真实原因吞掉，
        // 必须优先展示 e.message。
        this.grantErr = (e && e.message) ? e.message : '网络异常，请重试';
      } finally {
        this.granting = false;
      }
    }
  }
};
</script>

<style scoped>
.minor-card {
  position: fixed;
  right: 16px;
  bottom: 84px;
  z-index: 2000;
  width: 240px;
  box-sizing: border-box;
  background: #fbfaf6;
  border: 1px solid #e7e2d6;
  border-radius: 18px;
  padding: 14px 16px 16px;
  box-shadow: 0 10px 28px rgba(40, 35, 25, 0.12);
  font-family: inherit;
}
.minor-card.is-curfew {
  background: #f6f6fb;
  border-color: #dcdcec;
}
.mc-head {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
}
.mc-badge {
  width: 26px;
  height: 26px;
  border-radius: 8px;
  background: #1f7a5a;
  color: #fff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
}
.minor-card.is-curfew .mc-badge {
  background: #3a3a6b;
}
.mc-shield {
  display: block;
}
.mc-title {
  flex: 1 1 auto;
  font-size: 14px;
  font-weight: 600;
  color: #2b2b2b;
}
.mc-fold {
  border: none;
  background: transparent;
  color: #9a948a;
  font-size: 12px;
  padding: 2px 2px;
  cursor: pointer;
}
.mc-fold:hover {
  color: #6b665d;
}
.mc-label {
  font-size: 12px;
  color: #9a948a;
  margin-bottom: 2px;
}
.mc-time {
  font-size: 24px;
  font-weight: 700;
  letter-spacing: 0.5px;
  color: #1f7a5a;
  font-variant-numeric: tabular-nums;
  line-height: 1.2;
}
.minor-card.is-curfew .mc-time {
  color: #3a3a6b;
}
.mc-note {
  margin-top: 8px;
  font-size: 11px;
  line-height: 1.5;
  color: #a39d92;
}
.mc-tab {
  position: fixed;
  right: 16px;
  bottom: 84px;
  z-index: 2000;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: 1px solid #e7e2d6;
  background: #fbfaf6;
  border-radius: 999px;
  padding: 9px 15px;
  cursor: pointer;
  color: #1f7a5a;
  font-size: 13px;
  font-weight: 600;
  box-shadow: 0 6px 18px rgba(40, 35, 25, 0.1);
  font-family: inherit;
}
.mc-tab.is-curfew {
  color: #3a3a6b;
  border-color: #dcdcec;
}
.mc-tab-text {
  font-variant-numeric: tabular-nums;
}
.minorCardIn-enter-active {
  transition: opacity .28s cubic-bezier(.22, .61, .36, 1), transform .28s cubic-bezier(.22, .61, .36, 1);
}
.minorCardIn-leave-active {
  transition: opacity .18s ease, transform .18s ease;
}
.minorCardIn-enter-from,
.minorCardIn-leave-to {
  opacity: 0;
  transform: translateY(14px);
}

.minor-lock-mask {
  position: fixed;
  inset: 0;
  z-index: 3000;
  background: rgba(0, 0, 0, 0.82);
  display: flex;
  align-items: center;
  justify-content: center;
}
.minor-lock-card {
  width: 320px;
  max-width: 86vw;
  background: #fff;
  border-radius: 16px;
  padding: 32px 24px;
  text-align: center;
  color: #333;
}
.minor-lock-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 10px;
  color: #27ae60;
}
.minor-lock-card h3 {
  margin: 0 0 12px;
  font-size: 18px;
  color: #27ae60;
}
.minor-lock-card p {
  margin: 0 0 20px;
  font-size: 14px;
  line-height: 1.7;
  color: #666;
}
.minor-lock-btn {
  border: none;
  border-radius: 999px;
  padding: 10px 32px;
  background: #27ae60;
  color: #fff;
  font-size: 15px;
  cursor: pointer;
}
.minor-lock-btn:hover {
  background: #219a52;
}
.minor-lock-btn.primary {
  background: #27ae60;
  margin-top: 16px;
}
.minor-lock-btn.primary:disabled {
  background: #9ad9b6;
  cursor: not-allowed;
}
.minor-lock-btn.ghost {
  background: transparent;
  color: #888;
  border: 1px solid #ddd;
  margin-top: 12px;
}
.minor-lock-btn.ghost:hover {
  background: #f5f5f5;
}
.minor-lock-input {
  width: 100%;
  box-sizing: border-box;
  margin-top: 16px;
  padding: 10px 14px;
  border: 1px solid #ddd;
  border-radius: 10px;
  font-size: 14px;
  outline: none;
}
.minor-lock-input:focus {
  border-color: #27ae60;
}
.minor-lock-err {
  margin: 8px 0 0;
  font-size: 13px;
  color: #e74c3c;
}
.minor-lock-tip {
  margin: 0 0 4px;
  font-size: 13px;
  color: #888;
  line-height: 1.6;
}
</style>
